import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ClimatePanel from "@/components/ClimatePanel";
import WeatherExtras from "@/components/WeatherExtras";
import DiagnosisCard from "@/components/DiagnosisCard";
import { api, API } from "@/lib/api";
import { getLiveClimate } from "@/lib/weather";
import { useAuth } from "@/contexts/AuthContext";
import { Camera, FilmStrip, ArrowsClockwise, X, Upload, Sparkle, ArrowLeft, MapPin, Plant as PlantIcon, FilePdf, Mountains, Cherries, Flower, Grains, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const VIDEO_FRAMES = 5; // number of frames captured for video-scan mode
const VIDEO_INTERVAL_MS = 700;

function stripDataUrl(dataUrl) {
  const idx = dataUrl.indexOf(",");
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
}

export default function Scan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [kind, setKind] = useState("plant"); // plant | soil | fruit | flower
  const [isPro, setIsPro] = useState(false);
  const [mode, setMode] = useState("photo"); // photo | video
  const [facing, setFacing] = useState("environment");
  const [cameraError, setCameraError] = useState("");
  const [capturedPreviews, setCapturedPreviews] = useState([]); // dataURLs for preview
  const [capturedRaw, setCapturedRaw] = useState([]); // base64 strings
  const [climate, setClimate] = useState(null);
  const [locationLabel, setLocationLabel] = useState(null);
  const [coords, setCoords] = useState(null); // { lat, lon }
  const [climateLoading, setClimateLoading] = useState(false);
  const [climateError, setClimateError] = useState("");
  const [note, setNote] = useState("");
  const [plantId, setPlantId] = useState("");
  const [plants, setPlants] = useState([]);
  const [recording, setRecording] = useState(false);
  const [recordCountdown, setRecordCountdown] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  // Load user's plants for the optional plant picker
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plants");
        setPlants(data.items || []);
      } catch {}
      try {
        const { data } = await api.get("/stats");
        setIsPro(!!data.is_pro);
      } catch {}
    })();
  }, []);

  const KIND_TABS = [
    { id: "plant",  label: "Plant",  icon: PlantIcon, pro: false },
    { id: "soil",   label: "Soil",   icon: Mountains, pro: true },
    { id: "fruit",  label: "Fruit",  icon: Cherries,  pro: true },
    { id: "flower", label: "Flower", icon: Flower,    pro: true },
    { id: "seed",   label: "Seed",   icon: Grains,    pro: true },
  ];

  const onPickKind = (id) => {
    const tab = KIND_TABS.find((t) => t.id === id);
    if (tab?.pro && !isPro) {
      toast.info("Soil, Fruit & Flower scans are part of the Grower plan.");
      navigate("/pricing");
      return;
    }
    setKind(id);
    setResult(null);
    if (id !== "plant") setPlantId("");
  };

  // start camera
  const startCamera = useCallback(async (mode = facing) => {
    setCameraError("");
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (e) {
      console.error(e);
      setCameraError(e.message || "Camera access denied. You can also upload images below.");
    }
  }, [facing]);

  useEffect(() => {
    startCamera(facing);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line
  }, [facing]);

  // Load climate for a specific coordinate. Used by both geolocation and city search.
  const loadClimateFor = useCallback(async (lat, lon, presetLabel) => {
    setClimateLoading(true);
    setClimateError("");
    setCoords({ lat, lon });
    try {
      const data = await getLiveClimate(lat, lon);
      setClimate(data.climate);
      setLocationLabel(presetLabel || data.location_label);
      if (!data.climate && data.warning) {
        setClimateError(data.warning);
      }
    } catch {
      setClimateError("Live weather is temporarily unavailable. Your scan will still work.");
    } finally {
      setClimateLoading(false);
    }
  }, []);

  // fetch climate from geolocation
  const fetchClimate = useCallback(() => {
    if (!navigator.geolocation) {
      setClimateError("Geolocation not supported. Climate data unavailable.");
      return;
    }
    setClimateLoading(true);
    setClimateError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        loadClimateFor(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        setClimateError(err.message || "Location permission denied. Continuing without climate.");
        setClimateLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  }, [loadClimateFor]);

  useEffect(() => {
    fetchClimate();
  }, [fetchClimate]);

  // Capture single frame from <video>
  const captureFrame = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return null;
    const w = v.videoWidth || 720;
    const h = v.videoHeight || 1280;
    // Downscale max width to 900 to keep payload reasonable
    const maxW = 900;
    const scale = w > maxW ? maxW / w : 1;
    c.width = Math.round(w * scale);
    c.height = Math.round(h * scale);
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.85);
  };

  const onSinglePhoto = () => {
    const data = captureFrame();
    if (!data) return;
    setCapturedPreviews((arr) => [...arr, data]);
    setCapturedRaw((arr) => [...arr, stripDataUrl(data)]);
  };

  const onVideoScan = async () => {
    if (recording) return;
    setRecording(true);
    setCapturedPreviews([]);
    setCapturedRaw([]);
    const previews = [];
    const raws = [];
    for (let i = 0; i < VIDEO_FRAMES; i++) {
      setRecordCountdown(VIDEO_FRAMES - i);
      const data = captureFrame();
      if (data) {
        previews.push(data);
        raws.push(stripDataUrl(data));
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, VIDEO_INTERVAL_MS));
    }
    setRecordCountdown(0);
    setRecording(false);
    setCapturedPreviews(previews);
    setCapturedRaw(raws);
    toast.success(`Captured ${previews.length} frames for analysis`);
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8);
    if (!files.length) return;
    const reads = await Promise.all(
      files.map(
        (f) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(f);
          })
      )
    );
    setCapturedPreviews((arr) => [...arr, ...reads]);
    setCapturedRaw((arr) => [...arr, ...reads.map(stripDataUrl)]);
  };

  const onReset = () => {
    setCapturedPreviews([]);
    setCapturedRaw([]);
    setResult(null);
  };

  const onAnalyze = async () => {
    if (!capturedRaw.length) {
      toast.error("Capture or upload at least one image first.");
      return;
    }
    setAnalyzing(true);
    setResult(null);
    try {
      const { data } = await api.post("/scans/analyze", {
        images: capturedRaw,
        capture_mode: mode,
        kind: kind,
        climate: climate,
        location_label: locationLabel,
        user_note: note,
        plant_id: kind === "plant" ? (plantId || null) : null,
      });
      setResult(data);
      toast.success("Diagnosis ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50" data-testid="scan-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-5">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <div className="font-mono-data text-mute">new scan</div>
              <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight">
                {kind === "plant" && "Point at the plant."}
                {kind === "soil" && "Point at the soil."}
                {kind === "fruit" && "Point at the fruit."}
                {kind === "flower" && "Point at the flower."}
                {kind === "seed" && "Point at the seeds."}
              </h1>
            </div>
            <div className="inline-flex p-1 bg-sand-100 rounded-full border border-sand-200" role="tablist">
              <button
                onClick={() => setMode("photo")}
                data-testid="mode-photo"
                className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 transition ${mode === "photo" ? "bg-forest text-white" : "text-ink"}`}
              >
                <Camera size={14} weight="duotone" /> Photo
              </button>
              <button
                onClick={() => setMode("video")}
                data-testid="mode-video"
                className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 transition ${mode === "video" ? "bg-forest text-white" : "text-ink"}`}
              >
                <FilmStrip size={14} weight="duotone" /> Video scan
              </button>
            </div>
          </div>

          {/* Kind selector */}
          <div className="flex flex-wrap gap-2" data-testid="kind-tabs">
            {KIND_TABS.map((t) => {
              const Icon = t.icon;
              const active = kind === t.id;
              const locked = t.pro && !isPro;
              return (
                <button
                  key={t.id}
                  onClick={() => onPickKind(t.id)}
                  data-testid={`kind-${t.id}`}
                  className={`group relative px-4 py-2 rounded-2xl border text-sm flex items-center gap-2 transition
                    ${active ? "bg-forest text-white border-forest" : "bg-white text-ink border-sand-200 hover:border-forest/40"}`}
                >
                  <Icon size={16} weight="duotone" />
                  <span>{t.label}</span>
                  {t.pro && (
                    locked ? (
                      <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-ochre/20 text-ochre-dark">
                        <Lock size={10} weight="bold" /> Pro
                      </span>
                    ) : (
                      <span className={`ml-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${active ? "bg-white/20 text-white" : "bg-forest/10 text-forest"}`}>Pro</span>
                    )
                  )}
                </button>
              );
            })}
          </div>

          {/* Camera viewport */}
          <div className="relative rounded-3xl overflow-hidden bg-ink shadow-glass aspect-[3/4] sm:aspect-[4/3]" data-testid="camera-viewport">
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute inset-6 border-2 border-dashed border-white/60 rounded-2xl pointer-events-none" />
            {recording && (
              <div className="absolute inset-6 overflow-hidden rounded-2xl pointer-events-none">
                <div className="absolute left-0 right-0 h-[2px] bg-ochre shadow-[0_0_24px_6px_rgba(217,160,91,0.7)] animate-scan-sweep" />
              </div>
            )}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
              <span className="tag bg-white/90 backdrop-blur" data-testid="camera-mode-label">{kind} · {mode === "video" ? "video scan" : "photo"}</span>
              <button
                onClick={() => setFacing(facing === "environment" ? "user" : "environment")}
                className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-ink hover:bg-white"
                aria-label="Flip camera"
                data-testid="flip-camera"
              >
                <ArrowsClockwise size={18} weight="duotone" />
              </button>
            </div>
            {recording && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display text-7xl text-white drop-shadow-lg" data-testid="record-countdown">
                {recordCountdown}
              </div>
            )}
            {cameraError && (
              <div className="absolute inset-0 flex items-center justify-center bg-ink/90 text-white text-center p-6">
                <div>
                  <Camera size={32} weight="duotone" className="mx-auto mb-3 text-ochre" />
                  <div className="font-display text-2xl">Camera unavailable</div>
                  <p className="text-white/80 mt-1 text-sm">{cameraError}</p>
                  <p className="text-white/70 mt-2 text-sm">You can still upload images below.</p>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4">
            {mode === "photo" ? (
              <button onClick={onSinglePhoto} disabled={!!cameraError} data-testid="capture-photo-btn" className="btn-primary !px-8 !py-4 !text-base">
                <Camera size={18} weight="bold" /> Capture
              </button>
            ) : (
              <button onClick={onVideoScan} disabled={recording || !!cameraError} data-testid="capture-video-btn" className="btn-primary !px-8 !py-4 !text-base">
                <FilmStrip size={18} weight="bold" /> {recording ? "Scanning…" : "Start 4s scan"}
              </button>
            )}
            <label className="btn-secondary !text-sm cursor-pointer" data-testid="upload-label">
              <Upload size={14} weight="bold" /> Upload
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={onUpload}
                className="hidden"
                data-testid="upload-input"
              />
            </label>
          </div>

          {/* Captured previews */}
          {capturedPreviews.length > 0 && (
            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-data text-mute">captured · {capturedPreviews.length}</div>
                <button onClick={onReset} className="text-sm text-mute hover:text-terra flex items-center gap-1" data-testid="reset-captures">
                  <X size={14} /> Clear
                </button>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {capturedPreviews.map((src, i) => (
                  <div key={i} className="aspect-square rounded-lg overflow-hidden border border-sand-200">
                    <img src={src} alt={`capture-${i}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {kind === "plant" && (
            <div>
              <label className="font-mono-data text-mute">Attach to plant (optional)</label>
              <select
                value={plantId}
                onChange={(e) => setPlantId(e.target.value)}
                data-testid="plant-picker"
                className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
              >
                <option value="">— Standalone scan —</option>
                {plants.map((p) => (
                  <option key={p.plant_id} value={p.plant_id}>
                    {p.nickname || p.common_name}{p.scientific_name ? ` · ${p.scientific_name}` : ""}
                  </option>
                ))}
              </select>
              {plants.length === 0 && (
                <p className="text-xs text-mute mt-1">Add plants in your collection to track repeat scans over time.</p>
              )}
            </div>
          )}

          <div>
            <label className="font-mono-data text-mute">Add a note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="user-note"
              rows={2}
              placeholder="e.g. Yellowing started 3 days ago after a heatwave"
              className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
            />
          </div>

          <button
            onClick={onAnalyze}
            disabled={analyzing || capturedRaw.length === 0}
            data-testid="analyze-btn"
            className="btn-primary w-full justify-center !bg-terra hover:!bg-terra-dark !py-4 !text-base"
          >
            <Sparkle size={18} weight="bold" /> {analyzing ? "Diagnosing with GPT-5.5…" : "Analyze with climate context"}
          </button>
        </div>

        {/* Side panel */}
        <aside className="lg:col-span-5 space-y-5">
          <ClimatePanel climate={climate} locationLabel={locationLabel} loading={climateLoading} error={climateError} />
          <button onClick={fetchClimate} data-testid="refresh-climate" className="text-sm text-forest hover:underline inline-flex items-center gap-1">
            <MapPin size={12} weight="duotone" /> Refresh climate
          </button>

          <WeatherExtras
            lat={coords?.lat}
            lon={coords?.lon}
            locationLabel={locationLabel}
            plants={plants}
            isAuthed={!!user}
            onPickLocation={({ latitude, longitude, label }) => loadClimateFor(latitude, longitude, label)}
          />

          {analyzing && (
            <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6 text-center" data-testid="analyzing-panel">
              <div className="font-display text-2xl text-forest">Reading the leaves…</div>
              <p className="text-mute mt-1 text-sm">Fusing image, climate context, and pathology priors.</p>
              <div className="mt-4 h-2 bg-sand-200 rounded-full overflow-hidden">
                <div className="h-full bg-forest animate-pulse-soft w-3/4" />
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <DiagnosisCard analysis={result.analysis} climate={result.climate} kind={result.kind || kind} />
              <div className="flex items-center justify-between">
                <button onClick={onReset} data-testid="new-scan-btn" className="btn-secondary !text-sm"><ArrowLeft size={14} /> New scan</button>
                <button onClick={() => navigate(`/scan/${result.scan_id}`)} data-testid="open-result-detail" className="btn-primary !text-sm">Open full report</button>
              </div>
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}
